import React, { useState, useEffect } from "react";
import {
  Flex,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  Box,
  Input,
  Select,
  useToast,
  FormLabel,
} from "@chakra-ui/react";
import Card from "components/card/Card";
import fetchWithToken from "views/auth/signIn/axiosInstance";

export const FeedbackList = () => {
  const [feedbacks, setFeedbacks] = useState([]);
  const [filteredFeedbacks, setFilteredFeedbacks] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [sentiment, setSentiment] = useState(""); // Sentiment filter
  const [priority, setPriority] = useState(""); // Priority filter
  const [userId, setUserId] = useState(""); // User ID filter
  const [startDate, setStartDate] = useState(""); // Start date filter
  const [endDate, setEndDate] = useState(""); // End date filter
  const [skip, setSkip] = useState(0); // Pagination: skip
  const [limit, setLimit] = useState(20); // Pagination: limit
  const [total, setTotal] = useState(0); // Total number of feedbacks
  const [error, setError] = useState("");
  const toast = useToast();

  const fetchFeedbacks = async () => {
    try {
      const params = new URLSearchParams();

      // Add filters only if they have values
      if (sentiment) params.append("sentiment", sentiment);
      if (priority) params.append("priority", priority);
      if (userId) params.append("user_id", userId);
      if (startDate) params.append("start_date", startDate);
      if (endDate) params.append("end_date", endDate);

      // Add pagination parameters
      params.append("skip", skip);
      params.append("limit", limit);

      const url = `${process.env.REACT_APP_API_URL3}/feedbacks?${params.toString()}`;
      console.log("Feedbacks Request URL:", url);

      const response = await fetchWithToken(url);
      if (!response) {
        throw new Error("Failed to fetch feedbacks");
      }

      setFeedbacks(response.feedbacks);
      setFilteredFeedbacks(response.feedbacks);
      setTotal(response.total); // Update the total number of feedbacks
    } catch (error) {
      console.error("Error fetching feedbacks:", error);
      setError("Failed to fetch feedbacks");
    }
  };

  useEffect(() => {
    fetchFeedbacks();
  }, [sentiment, priority, userId, startDate, endDate, skip, limit]); // Refetch feedbacks when filters or pagination changes

  const handleSearch = (event) => {
    const query = event.target.value;
    setSearchQuery(query);
    const filtered = feedbacks.filter((feedback) =>
      feedback.comment?.toLowerCase().includes(query.toLowerCase())
    );
    setFilteredFeedbacks(filtered);
  };

  const handleNextPage = () => {
    setSkip(skip + limit);
  };

  const handlePreviousPage = () => {
    if (skip >= limit) {
      setSkip(skip - limit);
    }
  };

  return (
    <Card
      direction="column"
      w="100%"
      px="0px"
      overflowX={{ sm: "scroll", lg: "hidden" }}
    >
      <Flex px="20px" justify="space-between" mb="20px" align="center" wrap="wrap" gap="20px">
        <Text
          color="gray.800"
          fontSize="22px"
          fontWeight="700"
          lineHeight="100%"
          flexBasis="100%"
        >
          Feedback List
        </Text>
        <Flex gap="5px" flexWrap="wrap" justify="flex-start" w="100%">
          <Box>
            <FormLabel fontSize="sm">Search Feedbacks</FormLabel>
            <Input
              placeholder="Search feedbacks..."
              value={searchQuery}
              onChange={handleSearch}
              size="sm"
              w={{ base: "100%", md: "160px" }}
            />
          </Box>

          <Box>
            <FormLabel fontSize="sm">Filter by Sentiment</FormLabel>
            <Select
              placeholder="Select sentiment"
              value={sentiment}
              onChange={(e) => setSentiment(e.target.value)}
              size="sm"
              w={{ base: "100%", md: "160px" }}
            >
              <option value="good">Good</option>
              <option value="bad">Bad</option>
            </Select>
          </Box>

          <Box>
            <FormLabel fontSize="sm">Filter by Priority</FormLabel>
            <Select
              placeholder="Select priority"
              value={priority}
              onChange={(e) => setPriority(e.target.value)}
              size="sm"
              w={{ base: "100%", md: "160px" }}
            >
              <option value="low">Low</option>
              <option value="high">High</option>
            </Select>
          </Box>

          <Box>
            <FormLabel fontSize="sm">Filter by User ID</FormLabel>
            <Input
              placeholder="Enter user ID"
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              size="sm"
              w={{ base: "100%", md: "160px" }}
            />
          </Box>

          <Box>
            <FormLabel fontSize="sm">Start Date</FormLabel>
            <Input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              size="sm"
              w={{ base: "100%", md: "160px" }}
            />
          </Box>

          <Box>
            <FormLabel fontSize="sm">End Date</FormLabel>
            <Input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              size="sm"
              w={{ base: "100%", md: "160px" }}
            />
          </Box>

          <Box>
            <FormLabel fontSize="sm">Items per Page</FormLabel>
            <Select
              placeholder="Select limit"
              value={limit}
              onChange={(e) => setLimit(Number(e.target.value))}
              size="sm"
              w={{ base: "100%", md: "70px" }}
            >
              <option value="10">10</option>
              <option value="20">20</option>
              <option value="50">50</option>
              <option value="100">100</option>
            </Select>
          </Box>
        </Flex>

      </Flex>
      <Box overflowY="auto" maxHeight="400px">
        <Table variant="simple" color="gray.500" mb="24px">
          <Thead>
            <Tr bg="rgb(69, 50, 240)">
              <Th textColor="white">Index</Th>
              <Th textColor="white">Message</Th>
              <Th textColor="white">Sentiment</Th>
              <Th textColor="white">Priority</Th>
              <Th textColor="white">User ID</Th>
              <Th textColor="white">Date</Th>
            </Tr>
          </Thead>
          <Tbody>
            {filteredFeedbacks.map((feedback, index) => (
              <Tr key={feedback.feedback_id}>
                <Td>{index + 1}</Td>
                <Td>{feedback.message_content || "N/A"}</Td>
                <Td>{feedback.sentiment || "N/A"}</Td>
                <Td>{feedback.priority || "N/A"}</Td>
                <Td>{feedback.user_id || "N/A"}</Td>
                <Td>
                  {feedback.timestamp
                    ? new Intl.DateTimeFormat("en-US", {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                        second: "2-digit",
                      }).format(new Date(feedback.timestamp))
                    : "N/A"}
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>
      <Flex justify="space-between" px="25px" mt="10px">
        <Button
          size="sm"
          onClick={handlePreviousPage}
          isDisabled={skip === 0}
          colorScheme="blue"
        >
          Previous
        </Button>
        <Button
          size="sm"
          onClick={handleNextPage}
          isDisabled={total === 0 || skip + limit >= total} // Disable if total is 0 or no more pages
          colorScheme="blue"
        >
          Next
        </Button>
      </Flex>
      {error && (
        <Box mt="4">
          <Text color="red.500">{error}</Text>
        </Box>
      )}
    </Card>
  );
};
